import { useCallback, useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";
import Reveal from "./Reveal";
import { IMAGES } from "../data/site";

/** One draggable before/after comparison. */
const CompareSlider = ({ before, after, altBefore, altAfter, testid }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e) => { if (dragging.current) update(e.clientX); };
  const stop = () => { dragging.current = false; };
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") { setPos((p) => Math.max(4, p - 4)); e.preventDefault(); }
    if (e.key === "ArrowRight") { setPos((p) => Math.min(96, p + 4)); e.preventDefault(); }
  };

  return (
    <div
      ref={ref}
      data-testid={testid}
      className="relative overflow-hidden rounded-[2rem] shadow-[0_30px_60px_-20px_rgba(7,22,44,0.3)] select-none touch-none cursor-ew-resize aspect-[4/5]"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerCancel={stop}
    >
      {/* AFTER (base layer) */}
      <img src={after} alt={altAfter} className="absolute inset-0 w-full h-full object-cover" draggable="false" loading="lazy" />
      {/* BEFORE (clipped to the left of the handle) */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={before} alt={altBefore} className="absolute inset-0 w-full h-full object-cover" draggable="false" loading="lazy" />
      </div>

      <span className="absolute top-4 left-4 rounded-full bg-brand-ink/70 backdrop-blur-sm text-white text-xs font-bold uppercase tracking-widest px-4 py-2" aria-hidden="true">
        Innan
      </span>
      <span className="absolute top-4 right-4 rounded-full bg-brand-blue text-white text-xs font-bold uppercase tracking-widest px-4 py-2" aria-hidden="true">
        Efter
      </span>

      <div
        role="slider"
        tabIndex={0}
        aria-label="Jämför innan och efter – dra eller använd piltangenterna"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        onKeyDown={onKeyDown}
        data-testid={`${testid}-handle`}
        className="absolute inset-y-0 -translate-x-1/2 flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
        style={{ left: `${pos}%` }}
      >
        <span className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5 bg-white shadow-[0_0_12px_rgba(7,22,44,0.4)]" aria-hidden="true" />
        <span className="relative h-12 w-12 rounded-full bg-white shadow-[0_8px_25px_rgba(7,22,44,0.35)] flex items-center justify-center" aria-hidden="true">
          <ChevronsLeftRight className="h-5 w-5 text-brand-blue" />
        </span>
      </div>
    </div>
  );
};

export const BeforeAfter = () => (
  <section className="bg-white" data-testid="before-after-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 lg:pb-32">
      <div className="max-w-2xl">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-blue">Innan och efter</p>
        </Reveal>
        <Reveal delay={0.06}>
          <h2 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-brand-ink leading-tight">
            Dra i reglaget – <span className="font-accent italic font-semibold text-brand-blue">se skillnaden själv.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Två riktiga uppdrag. Till vänster disiga glas – till höger kristallklart resultat.
          </p>
        </Reveal>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl">
        <Reveal>
          <CompareSlider
            before={IMAGES.beforeUterum}
            after={IMAGES.afterUterum}
            altBefore="Innan – disiga glas i uterum"
            altAfter="Efter – kristallklara glas i uterum"
            testid="before-after-slider"
          />
          <p className="mt-3 text-sm text-slate-500 text-center">Uterum – putsning av alla glaspartier</p>
        </Reveal>
        <Reveal delay={0.1}>
          <CompareSlider
            before={IMAGES.workOne}
            after={IMAGES.workTwo}
            altBefore="Innan – fönster med streck och smuts"
            altAfter="Efter – streckfritt fönster med ren karm"
            testid="before-after-slider-2"
          />
          <p className="mt-3 text-sm text-slate-500 text-center">Fönster – In och utsida med karmar</p>
        </Reveal>
      </div>
      <Reveal delay={0.2}>
        <p className="mt-5 text-sm text-slate-500">Dra med fingret eller musen – eller använd piltangenterna.</p>
      </Reveal>
    </div>
  </section>
);

export default BeforeAfter;
